// Phase 6.0 · HOME HERO 자유편성 슬라이더 (관리자 CMS 데이터 기반)
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

const FALLBACK = {
  hero_slide_id: '_fallback',
  title: '모든 라이브를 한 곳에',
  subtitle: 'TikTok · YouTube · Instagram 라이브 쇼핑을 LIVEMOA 에서',
  cta_label: '지금 LIVE 보기',
  cta_link: '/schedule',
  image_url_pc: '', image_url_mobile: '',
};

export default function HomeHeroSlider() {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const startX = useRef(null);

  useEffect(() => {
    api.get('/public/home/hero-slides').then((r) => {
      const items = r.data?.items || [];
      setSlides(items.length ? items : [FALLBACK]);
    }).catch(() => setSlides([FALLBACK]));
  }, []);

  const goto = useCallback((i) => {
    if (!slides.length) return;
    setIdx(((i % slides.length) + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length < 2) return;
    timerRef.current = setInterval(() => { setIdx((i) => (i + 1) % slides.length); }, 5000);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  if (!slides.length) return null;
  const s = slides[idx];

  const onTouchStart = (e) => { startX.current = e.touches?.[0]?.clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? 0) - startX.current;
    if (Math.abs(dx) > 40) { if (dx > 0) goto(idx - 1); else goto(idx + 1); }
    startX.current = null;
  };

  const bgPc = s.image_url_pc;
  const bgMob = s.image_url_mobile || s.image_url_pc;

  return (
    <section className="relative border-b border-black/5 dark:border-white/10 overflow-hidden" data-testid="home-hero-slider"
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-2xl overflow-hidden mt-3 sm:mt-4 mb-3 sm:mb-4">
          <div className="relative aspect-[16/6] sm:aspect-[16/5] bg-gradient-to-br from-[#FF3B30]/10 to-white dark:from-[#FF3B30]/20 dark:to-[#0A0A0A]">
            {bgPc && (
              <img src={bgPc} alt={s.title} loading="eager"
                className="absolute inset-0 w-full h-full object-cover hidden sm:block" />
            )}
            {bgMob && (
              <img src={bgMob} alt={s.title} loading="eager"
                className="absolute inset-0 w-full h-full object-cover sm:hidden" />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-transparent" />
            <div className="absolute inset-0 flex items-center px-6 sm:px-10">
              <div className="max-w-lg" data-testid={`hero-slide-${idx}`}>
                <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight text-white drop-shadow"
                    style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {s.title}
                </h1>
                {s.subtitle && <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/90 max-w-md">{s.subtitle}</p>}
                {s.cta_label && (
                  <Link to={s.cta_link || '#'}
                    className="mt-4 sm:mt-5 inline-block px-5 py-2.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-black text-sm shadow-lg transition-colors"
                    data-testid="hero-cta">{s.cta_label}</Link>
                )}
              </div>
            </div>
          </div>

          {slides.length > 1 && (
            <>
              <button onClick={() => goto(idx - 1)}
                className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white text-black items-center justify-center shadow"
                data-testid="hero-prev"><ChevronLeft size={18}/></button>
              <button onClick={() => goto(idx + 1)}
                className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white text-black items-center justify-center shadow"
                data-testid="hero-next"><ChevronRight size={18}/></button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => goto(i)}
                    aria-label={`슬라이드 ${i + 1}`}
                    className={`transition-all ${i === idx ? 'w-6 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'} rounded-full`}/>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
